import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserIp } from "redux/slices/userIpSlice";
import { setUserIpDetails } from "redux/slices/userIpDetailsSlice";
import {
  setUserNickname,
  setIsUserLoggedIn,
  setTelegramId,
  setUserRegDate,
  setUserEmail,
  setUserId,
  setSubscription,
  setSessionRestoreSettled,
} from "redux/slices/userDataSlice";
import { setDefaultApiKey } from "redux/slices/apiKeySlice";
import { getCookie } from "utils/cookieManagement";
import { resolveBootstrapApiKey } from "utils/auth/apiKeyStore";
import { classifyStoredSession } from "utils/auth/sessionRestore";
import { useLocation } from "react-router-dom";
import { getUsernameFromEmail } from "utils/manipulation/getUsernameFromEmail";
import useApiRequest from "hooks/api/useApiHook";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import { RootState } from "redux/store";
import { GetSubscription } from "api/GetSubscription";

export const AccountWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const subscription = useSelector((state: RootState) => state.userData.subscription)
  const apiKey = useSelector((state: RootState) => state.apiKey.apiKey)

  useEffect(() => {
    if (!subscription && apiKey !== process.env.REACT_APP_DEFAULT_API_KEY) {
      GetSubscription(apiKey).then(({ data }: any) => data && dispatch(setSubscription(data.id))).catch((error) => {/* console.error(error) */})
    }
  }, [subscription, apiKey, dispatch, pathname])

  const { data: ipData } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.IP,
  });

  const [userIp, setUserIpState] = useState<string>("");

  // Second request: the visitor's own IP geo for the banner (ip, city,
  // country, hostname, loc, organization, postal, region, time_zone — all in
  // the anonymous light report). Deliberately ANONYMOUS: no `apiToken`, so no
  // Authorization header. Signed in with the user key it would spend one of
  // the 30-per-minute free checks of a 0-request account on every page load
  // (spec 2026-10-20-website-api-settings, Stage 6.4). Pinned by a test.
  const { data: ipDetailsData } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.CHECK_IP,
    checkIpParams: userIp ? { queryIp: userIp } : undefined,
  }, userIp ? null : null); // Don't make request if there's no IP

  useEffect(() => {
    if (ipData) {
      // API returns structure: { status: "success", data: { ipv4: "...", ip_final: "..." } }
      const extractedIp = ipData.data?.ipv4 || ipData.data?.ip_final || ipData.data?.ip;
      
      if (extractedIp) {
        setUserIpState(extractedIp);
        dispatch(setUserIp(extractedIp));
      }
    }
  }, [ipData, dispatch]);

  useEffect(() => {
    if (ipDetailsData && ipDetailsData.data) {
      dispatch(setUserIpDetails(ipDetailsData.data));
    }
  }, [ipDetailsData, dispatch]);

  const userSessionData = getCookie("ses_userdata");
  const userSessionName = getCookie("ses_name");
  const userSessionId = getCookie("ses_sid");
  const userSessionToken = getCookie("ses_token");

  // The bootstrap api key is applied ONCE. localStorage is authoritative; the
  // `ses_userdata` cookie's key is only a fallback for legacy sessions (a stale
  // cookie variant must never override the stored key). Re-applying
  // it on every route change would overwrite a key the 401 recovery has just
  // stored (and loop with it); after bootstrap the key store
  // (utils/auth/apiKeyStore) is the only writer.
  const sessionCookieKeyApplied = useRef(false);

  useEffect(() => {
    // One classifier for "is there a stored session" (utils/auth/sessionRestore),
    // shared with useAuthInitialization.
    const session = classifyStoredSession();

    if (session?.kind === "cookie") {
      const { data, telegram_data, google_data } = session.userData;

      if (!sessionCookieKeyApplied.current) {
        sessionCookieKeyApplied.current = true;
        dispatch(setDefaultApiKey(resolveBootstrapApiKey(session.userData)));
      }
      dispatch(setUserRegDate(data.reg_date));
      dispatch(setUserId(data.user_id));
      dispatch(setUserNickname(data.username));
      dispatch(setUserEmail(data.email));

      dispatch(setIsUserLoggedIn(true));

      const isTelegramDataValid = !Array.isArray(telegram_data);

      if (isTelegramDataValid) {
        dispatch(setUserNickname(telegram_data.nickname));
        dispatch(setTelegramId(telegram_data.telegram_user_id));
      }

      const isGoogleDataValid = !Array.isArray(google_data);

      if (isGoogleDataValid) {
        dispatch(setUserEmail(google_data?.email));
      } else {
        dispatch(setUserEmail(data.email));
      }

      const isNicknameFromEmailValid =
        isGoogleDataValid && !isTelegramDataValid && !data.username;

      if (isNicknameFromEmailValid) {
        dispatch(setUserNickname(getUsernameFromEmail(google_data.email)));
      }
    } else if (session?.kind === "apiKey") {
      // Restore state from the stored API key
      dispatch(setDefaultApiKey(session.storedApiKey));
      
      // Check if there's saved user data in localStorage
      const savedUserData = localStorage.getItem('user_data');
      if (savedUserData) {
        try {
          const userData = JSON.parse(savedUserData);
          
          dispatch(setIsUserLoggedIn(true));
          dispatch(setUserEmail(userData.email));
          dispatch(setUserId(userData.userId));
          dispatch(setUserNickname(userData.userNickname));
          dispatch(setUserRegDate(userData.regDate));
        } catch (error) {
          // console.error("Error restoring user data:", error);
        }
      } else {
        // Set basic state
        dispatch(setIsUserLoggedIn(true));
      }
    } else {
      dispatch(setIsUserLoggedIn(false));
    }
    // The first pass has decided: route guards may now act on isUserLoggedIn.
    dispatch(setSessionRestoreSettled(true));
    // eslint-disable-next-line
  }, [
    userSessionData,
    userSessionName,
    userSessionId,
    userSessionToken,
    pathname,
  ]);

  return <>{children}</>;
};
